































































import mixins from 'vue-typed-mixins';
import { DataTableHeader } from 'vuetify';
import SurveyUtils, {
  SurveyGroup,
  SurveyParticipation,
  SurveyState,
} from '@/utils/SurveyUtils';
import ProviderUtils, {
  ServiceProvider,
  ServiceProviderType,
} from '@/utils/ProviderUtils';
import DashboardUtils from '@/utils/DashboardUtils';
import OversightUtils, { OversightOrganization } from '@/utils/OversightUtils';

interface SurveyParticipationFilters {
  organizations: {
    list?: OversightOrganization[];
    selected?: OversightOrganization[];
  };
  providerTypes: {
    list?: ServiceProviderType[];
    selected?: ServiceProviderType[];
  };
  submissionstatus: {
    list?: SurveyState[];
    selected?: SurveyState[];
  };
}

export default mixins().extend({
  computed: {
    permission() {
      return {
        filters: {
          organizations: this.$session.role?.type !== 'serviceprovider',
          providerTypes: true,
          submissionstatus: true,
        },
        delete:
          this.$session.role?.type === 'superadmin' ||
          this.$session.role?.type === 'admin',
      };
    },

    surveyGroupOptions(): any {
      return this.surveyGroups?.map((group) => ({
        text: group.name,
        value: group,
      }));
    },

    surveyGroup: {
      get(): SurveyGroup | undefined {
        const id = parseInt(this.$route.query.group as string);
        return this.surveyGroups?.find((group) => group.id === id);
      },
      async set(group: SurveyGroup) {
        const id = parseInt(this.$route.query.group as string);
        if (
          group.id !== id &&
          this.$route.name === 'survey-participation-list'
        ) {
          await this.$router.push({
            name: 'survey-participation-list',
            query: {
              ...this.$route.query,
              group: group?.id?.toString() || '',
            },
          });
        }
        this.loadParticipations();
      },
    },

    organizations(): any {
      return this.filters.organizations.list?.map((organization) => ({
        text: this.$translate(organization.name),
        value: organization,
      }));
    },

    providerTypes(): any {
      return this.filters.providerTypes.list?.map((type) => ({
        text: this.$translate(type.name),
        value: type,
      }));
    },

    submissionstatus(): any {
      return this.filters.submissionstatus.list?.map((submissionstate) => ({
        text: `${this.$translate(submissionstate.name)} (${
          submissionstate.key
        })`,
        value: submissionstate,
      }));
    },

    headers(): DataTableHeader<SurveyParticipation>[] {
      return [
        {
          text: this.$i18n.tc('model.survey.label'),
          value: `survey.name.public.${this.$i18n.locale}`,
        },
        {
          text: this.$i18n.tc('model.serviceProvider.label'),
          value: 'serviceprovider.name',
        },
        {
          text: this.$i18n.tc('model.serviceProviderType.label'),
          value: 'serviceprovider.type.name',
        },
        {
          text: this.$i18n.tc('model.oversightOrganization.label'),
          value: 'serviceprovider.parent.name',
        },
        {
          text: this.$i18n.tc('model.surveyParticipation.submissionstate'),
          value: 'submissionstate.name',
        },
        {
          text: this.$i18n.tc('model.surveyParticipation.progress'),
          value: 'progress',
        },
      ];
    },

    items(): SurveyParticipation[] | undefined {
      return this.participations?.filter((participation) => {
        let valid = true;
        if (
          valid &&
          this.filters.organizations.selected &&
          this.filters.organizations.selected.length > 0
        ) {
          valid =
            participation.serviceprovider.parent?.some((oversight) => {
              const index = this.filters.organizations.selected?.findIndex(
                (value) => value.id === oversight.id
              ) as number;
              return index >= 0;
            }) || false;
        }
        if (
          valid &&
          this.filters.providerTypes.selected &&
          this.filters.providerTypes.selected.length > 0
        ) {
          const index = this.filters.providerTypes.selected?.findIndex(
            (value) =>
              value.id === (participation.serviceprovider as any).type.id
          ) as number;
          valid = index >= 0;
        }
        if (
          valid &&
          this.filters.submissionstatus.selected &&
          this.filters.submissionstatus.selected.length > 0
        ) {
          const index = this.filters.submissionstatus.selected?.findIndex(
            (value) => value.key === participation.submissionstate.key
          ) as number;
          valid = index >= 0;
        }
        return valid;
      });
    },
  },

  data(): {
    surveyGroups?: SurveyGroup[];
    participations?: SurveyParticipation[];
    filters: SurveyParticipationFilters;
  } {
    return {
      surveyGroups: undefined,
      participations: undefined,
      filters: {
        organizations: {
          list: undefined,
          selected: undefined,
        },
        providerTypes: {
          list: undefined,
          selected: undefined,
        },
        submissionstatus: {
          list: undefined,
          selected: undefined,
        },
      },
    };
  },

  async mounted() {
    try {
      this.surveyGroups = await SurveyUtils.api.surveyGroup.list();
      if (this.surveyGroups?.length > 0 && this.$route.query.group) {
        this.surveyGroup = this.surveyGroups.find(
          (group) => group.id === parseInt(this.$route.query.group as string)
        );
      } else if (this.surveyGroups?.length > 0 && !this.$route.query.group) {
        this.surveyGroup = this.surveyGroups[0];
      }
    } catch (error) {
      this.$alert.show('mount', {
        type: 'error',
        messages: this.$handleAPIError(error),
      });
    }
  },

  watch: {
    '$route.query.group'() {
      if (
        this.surveyGroups &&
        this.surveyGroups.length > 0 &&
        !this.$route.query.group
      ) {
        this.surveyGroup = this.surveyGroups[0];
      }
    },
  },

  methods: {
    async loadParticipations() {
      try {
        const currentGroup = this.surveyGroup; // keep reference to the group being used for this call
        if (this.surveyGroup) {
          this.participations = undefined;
          this.filters.organizations = {};
          this.filters.providerTypes = {};
          this.filters.submissionstatus = {};

          const resp = await DashboardUtils.getParticipations(
            this.surveyGroup,
            this.$session.role?.type === 'serviceprovider'
          );

          // ensure that the selected group has not changed
          if (currentGroup === this.surveyGroup) {
            this.participations = resp.participations;
            this.filters.organizations = {
              list: resp.filter.oversights || [],
              selected: undefined,
            };
            this.filters.providerTypes = {
              list: resp.filter.sptypes || [],
              selected: undefined,
            };
            this.filters.submissionstatus = {
              list: resp.filter.states || [],
              selected: undefined,
            };
          }
        }
      } catch (error) {
        this.$alert.show('participations', {
          type: 'error',
          messages: this.$handleAPIError(error),
        });
      }
    },

    async select(participation: SurveyParticipation) {
      await this.$router.push({
        name: 'survey-report',
        params: {
          participantId: participation?.id?.toString() || '',
          path: '1',
        },
      });
    },

    async remove(participation: SurveyParticipation) {
      if (
        await this.$confirm(
          this.$i18n
            .t('validation.confirmation.delete', [
              this.$tc('model.surveyParticipation.label'),
            ])
            .toString()
        )
      ) {
        try {
          this.$alert.hide('remove');
          await SurveyUtils.api.surveyParticipation.remove(
            participation.id as number
          );
          const index = this.participations?.findIndex(
            (item) => item.id === participation.id
          );
          if (index && index >= 0) {
            this.participations?.splice(index, 1);
            this.participations = Array.from(this.participations || []);
          }

          this.$toast.push({
            type: 'success',
            text: this.$languageobj('validation.success.delete', {
              values: [this.$languageobj('model.surveyParticipation.label')],
            }),
          });
        } catch (error) {
          this.$alert.show('remove', {
            type: 'error',
            messages: this.$handleAPIError(error),
          });
        }
      }
    },
  },
});
